/**
 * GameLoop.ts
 * Game loop driven by requestAnimationFrame, delta-time based (§9.1).
 *
 *   requestAnimationFrame → update(delta) → render()
 *
 * Delta is supplied in seconds and clamped so a backgrounded tab (which pauses
 * rAF) doesn't produce a huge jump on resume.
 */

export type UpdateFn = (deltaSeconds: number) => void;
export type RenderFn = () => void;

const MAX_DELTA = 0.1; // seconds — clamp huge frame gaps

export class GameLoop {
  private running = false;
  private lastTime = 0;
  private rafId = 0;

  constructor(
    private readonly update: UpdateFn,
    private readonly render: RenderFn,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.rafId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  get isRunning(): boolean {
    return this.running;
  }

  private readonly frame = (now: number): void => {
    if (!this.running) return;
    let delta = (now - this.lastTime) / 1000;
    this.lastTime = now;
    if (delta > MAX_DELTA) delta = MAX_DELTA;

    this.update(delta);
    this.render();

    this.rafId = requestAnimationFrame(this.frame);
  };
}
